import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import ErrorIcon from '@mui/icons-material/Error';
import clsx from 'clsx';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Popper from '@mui/material/Popper';
import Fade from '@mui/material/Fade';
import {
  TemplateFieldDTO,
} from 'dtos';
import Checkbox from 'pages/Dashboard/components/Input/Checkbox';
import {
  useGetFieldById,
} from 'views/EMR/Settings/hooks';
import DeleteIcon from 'core/icons/delete';
import ToolTip from 'core/components/Tooltip';
import IconButton from '@mui/material/IconButton';
import Skeleton from 'core/components/Skeleton';

export type Props = {
  anchorElement?: HTMLSpanElement;
  field?: Partial<TemplateFieldDTO>;
  onClose: () => void;
  onChange: (selected: string[]) => void;
  selected?: string[];
  onRemove: () => void;
};

export default function FieldPopper({
  anchorElement,
  field,
  onClose,
  selected = [],
  onChange,
  onRemove,
}: Props) {
  const [selection, setSelection] = useState<string[]>(selected);
  const { data = {}, isFetching } = useGetFieldById(field?.templateFieldId);
  const { options } = data;

  const selectedSet = new Set<string>(selection);

  const deletedOptions = useMemo(() => {
    const deletedSet = new Set(selection);
    (options ?? []).forEach((option) => {
      if (deletedSet.has(option)) {
        deletedSet.delete(option);
      }
    });
    return Array.from(deletedSet);
  }, [options, selection]);

  const disabledDeletedOptions = (options ?? []).length === 0;

  const open = Boolean(anchorElement?.isConnected);

  useEffect(() => {
    if (!open) {
      onClose();
    }
  }, [open]);

  return open ? (
    <ClickAwayListener onClickAway={onClose}>
      <Popper
        open={open}
        transition
        anchorEl={anchorElement}
        id="field-popper"
        className="z-[2000]"
        placement="bottom-start"
      >
        {({ TransitionProps }) => (
          <Fade
            {...TransitionProps}
            timeout={350}
          >
            <div className="flex flex-col w-full gap-2 p-2 bg-white shadow-lg max-h-[200px] min-w-[64px] overflow-auto">
              {isFetching
                ? (
                  <>
                    <Skeleton className="h-[18px] !w-full" />
                    <Skeleton className="h-[18px] !w-full" />
                    <Skeleton className="h-[18px] !w-full" />
                  </>
                )
                : (
                  <>
                    {(options ?? []).length === 0 && (
                      <div className="flex items-center gap-2 text-red-600 text-xs">
                        <ErrorIcon className="!w-4 !h-4 text-inherit" />
                        <div className="font-semibold  uppercase">Field was deleted</div>
                        <ToolTip title="Remove it from the text">
                          <IconButton
                            color="error"
                            size="small"
                            onClick={onRemove}
                          >
                            <DeleteIcon className="!w-4 !h-4 !text-inherit" />
                          </IconButton>
                        </ToolTip>
                      </div>
                    )}
                    {(options ?? []).map((option) => (
                      <Checkbox
                        key={option}
                        color="secondary"
                        onChange={(checked) => {
                          if (checked) {
                            selectedSet.add(option);
                          } else {
                            selectedSet.delete(option);
                          }
                          const newSelected = Array.from(selectedSet);
                          setSelection(newSelected);
                          onChange(newSelected);
                        }}
                        label={option}
                        checked={selectedSet.has(option)}
                        id={option}
                      />
                    ))}
                    {(deletedOptions ?? []).map((option) => (
                      <ToolTip
                        key={option}
                        title={(
                          <p>
                            Option
                            &quot;
                            <span className="font-semibold">{option}</span>
                            &quot;
                            {' '}
                            was deleted from the field.
                            <br />
                            Unselecting it will remove it permanently from the list.
                          </p>
                        )}
                        hide={disabledDeletedOptions}
                        placement="right"
                        classes={{ popper: '!z-[2010]' }}
                      >
                        <span className={clsx({ 'cursor-not-allowed': disabledDeletedOptions })}>
                          <Checkbox
                            color="secondary"
                            onChange={() => {
                              selectedSet.delete(option);
                              const newSelected = Array.from(selectedSet);
                              setSelection(newSelected);
                              onChange(newSelected);
                            }}
                            className={clsx({ 'text-gray-600': !disabledDeletedOptions })}
                            disabled={disabledDeletedOptions}
                            label={option}
                            checked={selectedSet.has(option)}
                            id={option}
                          />
                        </span>
                      </ToolTip>
                    ))}
                  </>
                )}
            </div>
          </Fade>
        )}
      </Popper>
    </ClickAwayListener>
  ) : null;
}
